import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Shield, Award, AlertTriangle, Check, X } from 'lucide-react';

export default function DailyTaskPrinciplesValidation({ 
  tasks,
  open, 
  onClose, 
  currentUser,
  officers 
}) {
  const queryClient = useQueryClient();
  const [validations, setValidations] = useState({});

  const validateMutation = useMutation({
    mutationFn: async (validationData) => {
      // Process each task validation
      for (const [taskId, validation] of Object.entries(validationData)) {
        const task = tasks.find(t => t.id === taskId);
        if (!task) continue;

        // Update task with validation marker
        await base44.entities.DailyTask.update(taskId, {
          principles_validated: true,
          validation_notes: validation.notes
        });

        // Apply XP/SP changes to task owner
        const taskOfficer = officers.find(o => o.user_email === task.user_email);
        if (taskOfficer && (validation.xp_bonus > 0 || validation.xp_malus > 0 || validation.sp_bonus > 0 || validation.sp_malus > 0)) {
          const newTotalXp = (taskOfficer.total_xp || 0) + validation.xp_bonus - validation.xp_malus;
          
          await base44.entities.OfficerProfile.update(taskOfficer.id, {
            total_xp: Math.max(0, newTotalXp)
          });

          // Apply SP to Leadership skill
          if (validation.sp_bonus > 0 || validation.sp_malus > 0) {
            const updatedSkills = [...(taskOfficer.skills || [])];
            const leadershipIndex = updatedSkills.findIndex(s => s.name === 'Leadership');
            
            if (leadershipIndex >= 0) {
              const newXp = Math.max(0, (updatedSkills[leadershipIndex].xp || 0) + validation.sp_bonus - validation.sp_malus);
              const SP_PER_LEVEL = [0, 100, 250, 500, 1000, 2000];
              let newLevel = 1;
              for (let i = SP_PER_LEVEL.length - 1; i >= 0; i--) {
                if (newXp >= SP_PER_LEVEL[i]) {
                  newLevel = i;
                  break;
                }
              }
              updatedSkills[leadershipIndex] = {
                ...updatedSkills[leadershipIndex],
                xp: newXp,
                level: newLevel
              };
            } else {
              updatedSkills.push({
                name: 'Leadership',
                level: 1,
                xp: Math.max(0, validation.sp_bonus - validation.sp_malus)
              });
            }
            
            await base44.entities.OfficerProfile.update(taskOfficer.id, { skills: updatedSkills });
          }
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dailyTasks'] });
      queryClient.invalidateQueries({ queryKey: ['officers'] });
      onClose();
      setValidations({});
    }
  });

  const updateValidation = (taskId, field, value) => {
    setValidations(prev => ({
      ...prev,
      [taskId]: {
        ...(prev[taskId] || { status: 'validated', notes: '', xp_bonus: 0, sp_bonus: 0, xp_malus: 0, sp_malus: 0 }),
        [field]: value
      }
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    validateMutation.mutate(validations);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="cmdr-dailytaskprinciplesvalidation-dialog" className="bg-slate-900 border-slate-800 text-slate-100 max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-indigo-400" />
            Validate Daily Task Principles ({tasks.length} tasks)
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          {tasks.map((task) => {
            const validation = validations[task.id] || { status: 'validated', notes: '', xp_bonus: 0, sp_bonus: 0, xp_malus: 0, sp_malus: 0 };
            const officer = officers.find(o => o.user_email === task.user_email);

            return (
              <div key={task.id} className="p-4 bg-slate-950 rounded-lg border border-slate-800">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="text-sm font-semibold text-slate-200">{task.task_description}</div>
                    <div className="text-xs text-slate-500">{officer?.callsign || task.user_email}</div>
                  </div>
                  <Badge variant="outline" className="text-xs">Task #{task.task_order}</Badge>
                </div>

                {/* Officer's report */}
                {(task.principles_adhered?.length > 0 || task.red_line_considerations?.length > 0 || task.narrative_on_principles) && (
                  <div className="mb-4 p-3 bg-slate-900/50 rounded border border-slate-700">
                    {task.principles_adhered?.length > 0 && (
                      <div className="mb-2">
                        <Label className="text-xs text-slate-400">Principles:</Label>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {task.principles_adhered.map((p, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs bg-indigo-500/10 text-indigo-300 border-indigo-500/30">
                              {p}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {task.red_line_considerations?.length > 0 && (
                      <div className="mb-2">
                        <Label className="text-xs text-slate-400">Red Lines:</Label>
                        <div className="space-y-1 mt-1">
                          {task.red_line_considerations.map((rl, idx) => (
                            <div key={idx} className="text-xs flex items-center gap-2">
                              <span className="text-rose-300">{rl.red_line}</span>
                              <Badge variant="outline" className="text-xs">{rl.status?.replace('considered_', '')}</Badge>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {task.narrative_on_principles && (
                      <div>
                        <Label className="text-xs text-slate-400">Narrative:</Label>
                        <p className="text-xs text-slate-300 mt-1">{task.narrative_on_principles}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Validation inputs */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-slate-400 mb-1 block">XP Bonus/Malus</Label>
                    <div className="flex gap-2">
                      <Input data-testid="cmdr-text-xs-text-slate-400-mb-1-block-input"
                        type="number"
                        placeholder="Bonus"
                        value={validation.xp_bonus}
                        onChange={(e) => updateValidation(task.id, 'xp_bonus', parseInt(e.target.value) || 0)}
                        className="bg-slate-900 border-slate-700 h-8 text-xs"
                        min="0"
                      />
                      <Input data-testid="cmdr-dailytaskprinciplesvalidation-input"
                        type="number"
                        placeholder="Malus"
                        value={validation.xp_malus}
                        onChange={(e) => updateValidation(task.id, 'xp_malus', parseInt(e.target.value) || 0)}
                        className="bg-slate-900 border-slate-700 h-8 text-xs"
                        min="0"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-400 mb-1 block">SP Bonus/Malus (Leadership)</Label>
                    <div className="flex gap-2">
                      <Input data-testid="cmdr-text-xs-text-slate-400-mb-1-block-input-1"
                        type="number"
                        placeholder="Bonus"
                        value={validation.sp_bonus}
                        onChange={(e) => updateValidation(task.id, 'sp_bonus', parseInt(e.target.value) || 0)}
                        className="bg-slate-900 border-slate-700 h-8 text-xs"
                        min="0"
                      />
                      <Input data-testid="cmdr-dailytaskprinciplesvalidation-input-1"
                        type="number"
                        placeholder="Malus"
                        value={validation.sp_malus}
                        onChange={(e) => updateValidation(task.id, 'sp_malus', parseInt(e.target.value) || 0)}
                        className="bg-slate-900 border-slate-700 h-8 text-xs"
                        min="0"
                      />
                    </div>
                  </div>
                </div>

                <div className="mt-2">
                  <Textarea data-testid="cmdr-dailytaskprinciplesvalidation-textarea"
                    placeholder="Commander's notes..."
                    value={validation.notes}
                    onChange={(e) => updateValidation(task.id, 'notes', e.target.value)}
                    className="bg-slate-900 border-slate-700 h-16 text-xs"
                  />
                </div>
              </div>
            );
          })}

          <div className="flex gap-2 sticky bottom-0 bg-slate-900 pt-4 border-t border-slate-800">
            <Button data-testid="cmdr-cancel-btn" type="button" variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button type="submit" className="flex-1 bg-indigo-600 hover:bg-indigo-700">
              Apply All Validations
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}